import { db } from "../db/indexeddb";
import axios from "axios";
import { API_ENDPOINT } from "../../services/api/resources";

export class TWMRepository {

    getRecordKey(record = {}) {
        return String(
            record?.token ||
            record?.twm_token ||
            record?.three_way_matching?.token ||
            record?.match_code ||
            record?.twm_code ||
            record?.three_way_matching?.twm_code ||
            ""
        ).trim();
    }

    mergeTwmLists(baseList = [], approverList = []) {
        const approverMap = new Map(
            (Array.isArray(approverList) ? approverList : []).map((record) => [
                this.getRecordKey(record),
                record,
            ])
        );

        return (Array.isArray(baseList) ? baseList : []).map((record) => {
            const approverRecord = approverMap.get(this.getRecordKey(record));
            if (!approverRecord) return record;

            return {
                ...record,
                ...approverRecord,
                token:
                    record?.token ||
                    approverRecord?.token ||
                    approverRecord?.three_way_matching?.token ||
                    "",
                match_code:
                    record?.match_code ||
                    approverRecord?.match_code ||
                    approverRecord?.twm_code ||
                    approverRecord?.three_way_matching?.twm_code ||
                    "",
                twm_code:
                    record?.twm_code ||
                    approverRecord?.twm_code ||
                    approverRecord?.match_code ||
                    approverRecord?.three_way_matching?.twm_code ||
                    "",
                result_json:
                    record?.result_json ||
                    approverRecord?.result_json ||
                    approverRecord?.extracted_data ||
                    approverRecord?.three_way_matching_data ||
                    {},
                approvers:
                    approverRecord?.approvers ||
                    approverRecord?.three_way_matching?.approvers ||
                    record?.approvers ||
                    [],
            };
        });
    }

    // get cached twm

    async getFromCache(matchingType = "") {
        const rows = await db.threeWayMatching.toArray();
        const records = rows.map(row => row.data);
        if (!matchingType) return records;
        const normalize = value => String(value || "").replace(/[\s_-]/g, "").toUpperCase();
        return records.filter(record => {
            let result = record.result_json || record.extracted_data || record.three_way_matching?.result_json || {};
            if (typeof result === "string") {
                try { result = JSON.parse(result); } catch { result = {}; }
            }
            const type = record.matching_mode || record.three_way_matching?.matching_mode || result.matching_mode || result.matching_details?.matching_mode ||
                (result.payment_advice_data?.length ? "FOUR_WAY" : result.grn_data?.length ? "THREE_WAY" : "TWO_WAY");
            return normalize(type) === normalize(matchingType);
        });
    }

    // save to cache

    async saveToCache(list = [], clearOld = false) {
        if (!list.length) return;

        await db.transaction("rw", db.threeWayMatching, async () => {
            if (clearOld) await db.threeWayMatching.clear();

            const rows = list.map(twm => ({
                token: twm.token,
                match_code: twm.match_code,
                data: twm,
                lastSynced: Date.now()
            }));

            for (const row of rows) {
                await db.threeWayMatching.put(row);
            }
        });
    }

    // sync api

    normalizePagedResponse(data, fallbackPage = 1) {
        const payload = data ?? {};
        const isSavedRecordObject =
            payload &&
            typeof payload === "object" &&
            !Array.isArray(payload) &&
            (payload.result_json ||
                payload.extracted_data ||
                payload.three_way_matching_data ||
                payload.token ||
                payload.match_code ||
                payload.twm_code);

        const list =
            Array.isArray(payload) ? payload :
            Array.isArray(payload?.results) ? payload.results :
            Array.isArray(payload?.data) ? payload.data :
            Array.isArray(payload?.twm_list) ? payload.twm_list :
            Array.isArray(payload?.two_way_matching) ? payload.two_way_matching :
            Array.isArray(payload?.three_way_matching) ? payload.three_way_matching :
            Array.isArray(payload?.four_way_matching) ? payload.four_way_matching :
            Array.isArray(payload?.records) ? payload.records :
            isSavedRecordObject ? [payload] :
            [];

        const isArrayPayload = Array.isArray(payload);
        const metaSource = isArrayPayload ? {} : payload;
        const currentPage = Number(metaSource?.current_page ?? metaSource?.page ?? fallbackPage);
        const pageSize = Number(metaSource?.page_size ?? metaSource?.pageSize ?? list.length);
        const totalPages = Number(
            metaSource?.total_pages ?? metaSource?.page_count ?? metaSource?.totalPages ?? (isArrayPayload ? 1 : 0)
        );
        const totalCount = Number(
            metaSource?.total_count ?? metaSource?.totalCount ?? metaSource?.count ?? (isArrayPayload ? list.length : 0)
        );

        return {
            list,
            meta: {
                current_page: Number.isFinite(currentPage) && currentPage > 0 ? currentPage : fallbackPage,
                page_size: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : list.length,
                total_pages: Number.isFinite(totalPages) ? totalPages : 0,
                total_count: Number.isFinite(totalCount) ? totalCount : 0,
                has_next: Boolean(metaSource?.has_next ?? metaSource?.hasNext),
                has_previous: Boolean(metaSource?.has_previous ?? metaSource?.hasPrevious),
            },
        };
    }

    async fetchPageFromAPI(authToken, userToken, page = 1, search = "", status = "all", onBasePage, matchingType = "") {
        if (!navigator.onLine) {
            const cached = await this.getFromCache(matchingType);
            return this.normalizePagedResponse(cached, page);
        }

        const requestConfig = {
            headers: { Authorization: `Bearer ${authToken}` },
        };
        const payload = {
            user_token: userToken,
            search: String(search || "").trim(),
            page,
            status: String(status || "all").trim() || "all",
        };

        const endpoint = {
            TWO_WAY: API_ENDPOINT.get_two_way_matching,
            THREE_WAY: API_ENDPOINT.get_three_way_matching,
            FOUR_WAY: API_ENDPOINT.get_four_way_matching,
        }[matchingType] || API_ENDPOINT.get_three_way_matching;
        const listPayload = matchingType ? {
            user_token: userToken,
            page,
            ...(search ? { search: String(search).trim() } : {}),
            ...(status !== "all" ? { status } : {}),
        } : payload;
        const needsApprovers = !matchingType || matchingType === "THREE_WAY";
        const [baseResult, approverResult] = await Promise.allSettled([
            axios.post(
                endpoint,
                listPayload,
                requestConfig
            ).then((response) => {
                onBasePage?.(this.normalizePagedResponse(response?.data, page));
                return response;
            }),
            needsApprovers ? axios.post(
                API_ENDPOINT.get_three_way_matching_with_approvers,
                payload,
                requestConfig
            ) : Promise.resolve({ data: [] }),
        ]);

        if (baseResult.status === "rejected") throw baseResult.reason;
        if (approverResult.status === "rejected") throw approverResult.reason;
        const baseRes = baseResult.value;
        const approverRes = approverResult.value;
        const basePage = this.normalizePagedResponse(baseRes?.data, page);
        const approverPage = this.normalizePagedResponse(approverRes?.data, page);

        return {
            list: this.mergeTwmLists(basePage.list, approverPage.list),
            meta: basePage.meta,
        };
    }

    async syncFromAPI(authToken, userToken) {
        if (!navigator.onLine) {
            return this.getFromCache();
        }

        const { list } = await this.fetchPageFromAPI(authToken, userToken, 1);
        await this.saveToCache(list, true);
        return list;
    }


    // saveofflineAction

    async saveOfflineAction(action, twmToken, userToken) {
        await db.pendingTwmActions.add({
            action,
            twm_token: twmToken,
            user_token: userToken,
            createdAt: Date.now()

        }

        )
        console.log("Offline TWM action saved");

    }


    async syncPendingActions(authToken) {
  if (!navigator.onLine) return;

  const actions = await db.pendingTwmActions.toArray();

  for (const item of actions) {
    try {
      const formData = new FormData();
      formData.append("action", item.action);
      formData.append("user_token", item.user_token);
      formData.append("twm_token", item.twm_token);

      await axios.post(
        API_ENDPOINT.approve_reject_twm,
        formData,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      await db.pendingTwmActions.delete(item.id);

      console.log("Pending action synced");
    } catch (err) {
      console.error("Sync failed", err);
    }
  }
}

}

export const twmRepository = new TWMRepository();
